import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/biz-rule-engine.png";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "Business Rules Engine, BRMS & Enterprise Decision Platform | Avani Cimcon",
  description:
    "BizRuleEngine is Avani Cimcon's rules-engine heritage platform. It demonstrates enterprise decision logic, workflow control, change isolation and scalable business rule management.",
  keywords:
    "Biz Rule Engine, Biz Rules Engine: Business Rules Engine, Business Rules Management System (BRMS), Biz Rules Engine, Business Rules Engine, biztalk business rules engine, Business Rule Engine, Rules Engine, Open-source business rules engine, Biztalk business rules engine, business rule engine, business rules, SOA services, Soa Services oriented architecture, BPM/BRE Rule Engines, Biz Rules, Rules engine api, enterprise software engineering, legacy modernization, AI enablement, workflow orchestration, decision logic",
  canonical: "https://www.avanicimcon.com/biz-rule-engine",
  ogImage: "https://www.avanicimcon.com/images/banner/biz-rule-engine.png",
  hero: {
    eyebrow: "Product Heritage",
    heading: "BizRuleEngine",
    lead:
      "BizRuleEngine shows how Avani Cimcon separated business logic from application logic so enterprises could change rules without rewriting the whole system.",
    image: BannerImg,
    tags: ["BRMS", "Decision logic", "Workflow control", "Enterprise rules"],
    panelKicker: "Rules as architecture, not rules as a checkbox.",
    panelTitle: "A business rules platform built for regulated and dynamic enterprise workflows.",
    panelText:
      "The platform helped organizations externalize logic, reduce turnaround time and keep system behavior aligned with changing business conditions.",
    stats: [
      { value: "BRMS", label: "Business rules management system heritage" },
      { value: "SOA", label: "Service-oriented architecture context" },
      { value: "Rules", label: "Change control without code rewrites" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "A rules engine designed to keep business logic outside the application core",
    lead:
      "The page should show that Avani Cimcon understood how to make enterprise systems more adaptable by treating rules as a managed capability.",
    paragraphs: [
      "Business Rules Engine offered an architecturally pluggable solution with value-added features focused on segregating business rules from the client application and applying them across a wide variety of domains.",
      "The system helped organizations build calculations and decision logic that operated according to company data, which is exactly the kind of platform thinking modern enterprise buyers should see.",
    ],
    callout: {
      title: "What the page should prove",
      text: "This page is about enterprise decision architecture, not a generic software product pitch.",
      items: [
        "Rules externalized from application code",
        "Domain-agnostic decision support",
        "Lower change cost and better governance",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "Large enterprise applications needed changeable logic without risky rewrites",
    lead:
      "The platform was built for operational environments where rules change faster than code can safely be rewritten.",
    items: [
      {
        title: "Frequent policy change",
        text: "Insurance, banking and other large-scale applications needed logic adjustments without repeated developer cycles.",
      },
      {
        title: "Decision consistency",
        text: "The business wanted decisions to follow company data and policy, not whichever code branch happened to be embedded.",
      },
      {
        title: "Application longevity",
        text: "The architecture had to support long-lived systems that could evolve as the organization changed.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "Separated business logic from system logic to reduce change risk",
    lead:
      "That separation is a classic enterprise architecture move and a strong proof of engineering maturity.",
    paragraphs: [
      "Business experts could change rules dynamically without intervention from application developers, reducing turnaround time and keeping the operational stack stable.",
      "The core idea was to separate business logic from system logic so substantial behavior changes did not require substantial code rewrites.",
    ],
    callout: {
      title: "Architecture highlights",
      items: [
        "Pluggable rules services",
        "Rules stored outside application logic",
        "Reusable decision structures",
        "Applicable across many domains",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "The engine supported the kinds of rule operations enterprise teams actually need",
    lead:
      "Those capabilities show why the page is proof of platform engineering instead of just product naming.",
    items: [
      {
        title: "Dynamic rule changes",
        text: "Business experts could adjust rules without waiting for a development cycle.",
      },
      {
        title: "Reusable logic",
        text: "Rules could be coded, reused and scaled as needed across application areas.",
      },
      {
        title: "Wide domain applicability",
        text: "The design was intended for insurance, banking and other large workflow systems.",
      },
      {
        title: "Externalized decision control",
        text: "Business rules could live in database, XML, Java, HTML or DLL/JAR resources depending on the implementation context.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "The product solved the hard problem of keeping logic changeable and governed",
    lead:
      "This is where the page earns its keep as a heritage proof page.",
    items: [
      {
        title: "Rule sprawl",
        text: "Complex applications needed a place to manage rules without scattering logic through the code base.",
      },
      {
        title: "Workflow change pressure",
        text: "Applications with rapidly changing process logic needed faster response times.",
      },
      {
        title: "Scalable governance",
        text: "The challenge was not only technical flexibility, but also control over how rules were maintained and reused.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "Rules needed to scale with business growth without creating rewrite cycles",
    lead:
      "The platform was valuable because it made scale a governance problem, not a code explosion problem.",
    paragraphs: [
      "By allowing rules to be reused and scaled, the system supported organizations that expected policy changes, new logic and new deployment contexts over time.",
      "That same mindset maps directly to enterprise modernization programs where control, auditability and change velocity all matter at once.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Reduce developer dependency for routine rule updates",
        "Keep decision logic auditable and reusable",
        "Support regulated workflows with controlled change",
      ],
    },
  },
  integration: {
    eyebrow: "Technology & Integration Experience",
    title: "The rules engine worked as part of larger service-oriented systems",
    lead:
      "That integration context is important because it links the product to enterprise delivery and service composition.",
    paragraphs: [
      "The platform fit naturally into SOA services and BPM/BRE rule engine contexts, allowing businesses to connect decision logic to broader application ecosystems.",
      "Its web-based approach also made it easier to manage rules as a shared enterprise capability rather than a hidden implementation detail.",
    ],
    callout: {
      title: "Integration capabilities",
      items: [
        "SOA-aligned service integration",
        "Web-based administration",
        "Decision logic across heterogeneous systems",
        "Compatibility with large workflow environments",
      ],
    },
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "BizRuleEngine proves Avani Cimcon knows how to engineer changeable enterprise systems",
    lead:
      "That skill matters when clients need modernization without operational disruption.",
    items: [
      {
        title: "Governed change",
        text: "We know how to let business rules evolve without destabilizing the application core.",
      },
      {
        title: "Enterprise workflow design",
        text: "We can build systems where rules, approvals and exceptions are managed deliberately.",
      },
      {
        title: "Long-term maintainability",
        text: "We understand why externalized logic is a maintainability win, not just a feature.",
      },
      {
        title: "Modernization fit",
        text: "The same approach helps clients modernize legacy systems with less risk.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Rules engines are ideal candidates for modernization and service extraction",
    lead:
      "The product story naturally supports modernization because it already separates logic from application code.",
    paragraphs: [
      "Enterprises modernizing today can use the same idea to decouple business rules from older application layers and replace them in a controlled way.",
      "That is why the page should read as a proof of architectural judgment, not as a product brochure.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Keep business logic externalized",
        "Use rules to reduce hard-coded workflows",
        "Improve change governance around legacy systems",
        "Stage modernization around decision services",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI can sit on top of rule-based systems once the decision layer is clean",
    lead:
      "AI is useful here only after the operational rule structure is established.",
    items: [
      {
        title: "Decision assistance",
        text: "Use AI to recommend rule updates, identify exceptions and support analysts.",
      },
      {
        title: "Process intelligence",
        text: "Use AI to understand rule patterns and flag anomalies in enterprise workflows.",
      },
      {
        title: "Hybrid automation",
        text: "Blend AI suggestions with governed business rules for safer enterprise automation.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "Rules engines matter most where policies and operations change often",
    lead:
      "These links preserve the original vertical relevance and strengthen the enterprise story.",
    items: [
      { label: "Insurance", href: "/insurance", text: "Policy, claims and underwriting rules." },
      { label: "Legal", href: "/legal", text: "Case workflow and billing rules." },
      { label: "Healthcare", href: "/healthcare", text: "Operational and compliance logic." },
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Pricing and booking rules." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "The rules-engine story connects directly to the broader engineering portfolio",
    lead:
      "These pages reinforce the fact that BizRuleEngine is part of a real engineering ecosystem.",
    items: [
      { label: "DXchange", href: "/dxchange", text: "Integration and service exposure." },
      { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization planning and delivery." },
      { label: "Quality Assurance", href: "/quality-assurance", text: "Testing governed enterprise change." },
      { label: "CMS", href: "/cms", text: "Platform and content operations." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "BizRuleEngine proves the company can design software around enterprise control, not just code",
    lead:
      "That is the kind of experience enterprise buyers trust when they need changeable systems.",
    items: [
      {
        title: "Experience with regulated logic",
        text: "The product was relevant in domains where change needed to be controlled and auditable.",
      },
      {
        title: "Architecture maturity",
        text: "The system separated concerns in a way that still reads as modern enterprise design.",
      },
      {
        title: "Practical delivery",
        text: "The product reflects a real solution to a real enterprise problem, not a speculative concept.",
      },
      {
        title: "Modern relevance",
        text: "The same thinking supports modernization, process automation and AI-assisted operations today.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Bring this rules-engine mindset into your own modernization program",
    text:
      "If your enterprise needs better control over business logic, we can apply the same architectural discipline to your systems.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class BizRuleEngine extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default BizRuleEngine;
